import { useEffect, useId, useState } from 'react';

import { Button } from '@domcrm/embed-ui';

import { deleteMeAccount } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import { EmbedInput } from './embed-input';
import { EmbedMessageBlock } from './embed-message-block';

/** Слово-подтверждение: защищает от случайного удаления аккаунта. */
export const DELETE_ACCOUNT_CONFIRM_WORD = 'УДАЛИТЬ';

type Props = {
  open: boolean;
  accessToken: string;
  /** Названия организаций пользователя (из `me.memberships`) для явного предупреждения. */
  organizations: string[];
  onDeleted: () => void;
  onCancel: () => void;
};

export function DeleteAccountDialog({ open, accessToken, organizations, onDeleted, onCancel }: Props) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fieldId = useId();

  useEffect(() => {
    if (!open) {
      setConfirmText('');
      setErr(null);
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const canConfirm = confirmText.trim().toUpperCase() === DELETE_ACCOUNT_CONFIRM_WORD;
  const orgNames = organizations.filter((n) => n.trim() !== '');
  const orgWarning =
    orgNames.length > 0
      ? `Аккаунт будет удалён во всех организациях: ${orgNames.join(', ')}. `
      : '';

  async function submit(): Promise<void> {
    if (!canConfirm || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteMeAccount(accessToken);
      onDeleted();
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось удалить аккаунт';
      setErr(msg);
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-[var(--bg-elevated)] p-5 shadow-lg"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-account-dialog-title"
        aria-describedby="delete-account-dialog-desc"
      >
        <h2 id="delete-account-dialog-title" className="text-lg font-semibold text-[var(--text)]">
          Удалить аккаунт
        </h2>
        <p id="delete-account-dialog-desc" className="mt-2 text-sm text-[var(--text-muted)]">
          Профиль и данные удаляются безвозвратно; история бронирований у организации сохраняется в обезличенном виде.
          {orgWarning ? <span className="mt-1 block">{orgWarning}</span> : null}
        </p>

        <label htmlFor={fieldId} className="mt-4 block text-[13px] text-[var(--text-muted)]">
          Введите <span className="font-semibold text-[var(--error-text)]">{DELETE_ACCOUNT_CONFIRM_WORD}</span> для подтверждения
        </label>
        <EmbedInput
          id={fieldId}
          className="mt-1"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          disabled={busy}
          aria-label={`Введите ${DELETE_ACCOUNT_CONFIRM_WORD} для подтверждения`}
        />

        {err ? (
          <EmbedMessageBlock className="mt-3 w-full" variant="error" size="field" role="alert" aria-live="assertive">
            {err}
          </EmbedMessageBlock>
        ) : null}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" className="w-full sm:w-auto" disabled={busy} onClick={onCancel}>
            Отмена
          </Button>
          <Button
            type="button"
            variant="danger"
            className="w-full sm:w-auto"
            disabled={!canConfirm || busy}
            onClick={() => void submit()}
          >
            {busy ? 'Удаляем…' : 'Удалить аккаунт'}
          </Button>
        </div>
      </div>
    </div>
  );
}
